import * as HoverCard from '@radix-ui/react-hover-card'
import * as Popover from '@radix-ui/react-popover'
import {
	ChevronDownIcon,
	ExternalLinkIcon,
	GraduationCapIcon,
	InfoIcon,
	MapPinIcon,
	SendIcon,
	StethoscopeIcon,
	ThumbsDownIcon,
	ThumbsUpIcon,
	UserIcon,
	XIcon,
	ZapIcon,
} from 'lucide-react'
import {
	type MutableRefObject,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react'
import { ThreadVariantContext } from '#app/components/assistant-ui/thread.js'
import { useChatSessionId } from '#app/utils/chat-session.js'
import {
	queryLenBucket,
	trackSnabbsokClick,
	trackSnabbsokImpression,
	trackSnabbsokRating,
	trackSurfaceView,
	trackUIEvent,
} from '#app/utils/plausible.client.js'
import {
	REGIONS,
	shortSourceName,
	sourceColor,
	sourceFullName,
} from '#app/utils/search-lib'
import {
	MOVED_HINT_KEY,
	movedHintActive,
} from '#app/utils/search-moved-hint'
// Type-only import: the actual fetchSnabbSok runs SERVER-SIDE via the
// /resources/snabbsok loader (see useEffect below). This keeps the Typesense
// host + key out of the client bundle and satisfies prod's CSP connect-src
// 'self' + sentry + fathom.
import { type SnabbHit, type SnabbResults } from '#app/utils/snabbsok'
// Display-label rendering (displayTitle + REG-6 same-source disambiguation)
// lives in its own pure module so the eval harness (tools/eval/) renders the
// IDENTICAL label — shared source of truth, no drifting copy.
import {
	fireFeedbackClick,
	fireFeedbackExpand,
	snabbsokClickPayload,
	snabbsokSearchRequest,
	type SnabbsokClickPayload,
} from '#app/utils/snabbsok-feedback.client.ts'
import {
	disambiguateSameSource,
	displayTitle,
} from '#app/utils/snabbsok-labels'
import { isTestingMode } from '#app/utils/test-mode.client.js'

/**
 * Snabbsök — compact card surfacing IM + external links for the latest user
 * query. Renders inside the chat viewport above the messages, triggered by
 * `thread.run-start` (see thread.tsx).
 *
 * Design (the "source chip" direction, confirmed by the user 2026-05-21):
 * - A solid colour block on the LEFT of each result, coloured PER SOURCE (each
 *   source has its own distinct colour — see `sourceColor`), with the source
 *   abbreviation inside it for exact identification. Replaces the old gray
 *   right-badge; gives instant source recognition.
 * - Titles + sitelinks ONLY (no body text). Sitelinks (sub-pages of a grouped
 *   article) render under the title: 2 on mobile, up to 4 on desktop.
 * - Compact by default (3/section desktop, 2/section mobile). "Visa fler"
 *   reveals MORE on each click (progressive, +PROGRESSIVE_STEP per click) until
 *   the pool is exhausted, then the button hides. The user closes the card with
 *   the ✕.
 * - Desktop: hovering a result opens a preview card (full source name spelled
 *   out + full title + url), reusing the AI assistant's Radix HoverCard. Mobile
 *   has no hover — tap goes straight through to the source.
 *
 * Dismiss is per-query: clicking ✕ hides the card until the next user message
 * (parent passes a new `query`, which resets `dismissed`).
 */
// Compact caps differ by breakpoint (CSS-controlled); progressive reveal then
// takes over in JS once the user clicks "Visa fler".
const MOBILE_CAP = 2
const DESKTOP_CAP = 3
// Each "Visa fler" click reveals this many more rows per section ("fler och
// fler"); repeat until the section's pool is exhausted.
const PROGRESSIVE_STEP = 5

// The card always starts minimized: a plain text link "Visa sökträffar för
// frågan" under the first question bubble (rendered from UserMessage in
// thread.tsx), which expands into the full card in place. PR #42 (June 2026)
// minimized only 5+-word questions and PR #65 paused that; since 2026-09-29
// the start page mostly gets full clinical questions (the site's own AI box
// answers the quick lookups) and only ~11 % of shown cards got a click, with no
// clean break worth a word threshold — so every question gets the link.

export function SnabbSok({
	query,
	region,
}: {
	query: string
	/** Sidans regionval (?region=, se assistant-ui/region-picker.tsx). Kortet
	 * följer det och visar bara en etikett (RegionBadge): bytet görs i
	 * chattrutans pill, ett val för hela sidan. Kortets egen väljare,
	 * localStorage-valet och regiontipset hörde till det gamla läget utan
	 * sidans val och är borttagna (IM-61). */
	region: string
}) {
	const [results, setResults] = useState<SnabbResults | null>(null)
	const [dismissed, setDismissed] = useState(false)
	const [loading, setLoading] = useState(false)
	// Progressive expand. 0 = compact (CSS caps 2 mobile / 3 desktop). Each
	// "Visa fler" click increments by 1; rows shown per section then becomes
	// DESKTOP_CAP + step*PROGRESSIVE_STEP (JS-controlled, no CSS cap).
	const [step, setStep] = useState(0)
	// Every query starts as the compact bar. `expanded` flips when the user
	// clicks it — from then on the query renders the normal card. It is reset
	// per query (see the query-change effect below).
	const [expanded, setExpanded] = useState(false)
	// One surface_view per minimized query (the bar's analogue to
	// `lastTrackedQuery` for impressions).
	const lastMinimizedQuery = useRef<string | null>(null)
	// ThreadVariant drives the `location` prop on Plausible events ("embed"
	// when chat-page renders the iframe-mode variant, otherwise the standalone
	// start page). Matches the citation-bubble convention.
	const threadVariant = useContext(ThreadVariantContext)
	const plausibleLocation =
		threadVariant === 'embed' ? 'assistent_embed' : 'assistent_start_page'
	// One impression per QUERY (not per re-render and not per region switch —
	// a clinician toggling region after seeing results is the same impression).
	// Ref keyed on the last-tracked query string; reset implicitly when a new
	// query is fired by the parent.
	const lastTrackedQuery = useRef<string | null>(null)
	// Search-feedback IDs (Phase 0). The SESSION id lives in sessionStorage
	// (per-tab); the IMPRESSION id is minted per fetched query inside the
	// fetch effect and kept in a ref so every click handler reads the same
	// value without a re-render in between. The "started at" ref lets click
	// rows carry `time_since_impression_ms` — implicit-feedback signal for
	// later Typesense relevance tuning. Both ids are also plumbed into the
	// Plausible events as the join key between product metrics + Postgres.
	const sessionId = useChatSessionId()
	const impressionIdRef = useRef<string | null>(null)
	const impressionStartedAtRef = useRef<number | null>(null)

	// Reset per-query UI state (dismiss, progressive-expand step, bar-expanded)
	// whenever the query prop changes. With first-question-only triggering
	// (thread.tsx) the query is effectively fixed per mount — a new conversation
	// remounts this whole subtree — so today this only runs once on mount and is
	// idempotent with the useState initializers. Kept deliberately: it keeps
	// SnabbSok self-correct on a query change rather than coupling its
	// correctness to the parent's trigger gate (cheap, local, no assumption).
	useEffect(() => {
		setDismissed(false)
		setStep(0)
		setExpanded(false)
	}, [query])

	// Re-fetch when the query OR the selected region changes — the region
	// participates in ranking, so a region switch should re-rank live. The
	// call goes through /resources/snabbsok (same-origin, server-side), which
	// runs the Typesense multi_search and returns the full {im, ext} pool.
	// The AbortController cancels the CLIENT->proxy fetch on new-query /
	// region-switch so we discard stale results, but the proxy does NOT
	// forward the abort to its inner Typesense call (would race the polyfill
	// stream-cancel and crash dev). Inner ~100ms call runs to completion and
	// the response is dropped client-side. See snabbsok.ts loader comment
	// for the full rationale.
	//
	// Search-feedback: mint a new impressionId BEFORE the fetch (so the proxy
	// receives it on `?iid=` and writes the impression row with the same id we
	// later quote on clicks) and capture `Date.now()` as the impression's
	// started-at clock — `time_since_impression_ms` on click rows = "wall
	// time from results-fetch-start to user-click". The session id comes from
	// the per-tab `useChatSessionId` hook and is stable across re-fetches.
	useEffect(() => {
		if (!query.trim()) return
		const ctrl = new AbortController()
		// sid/iid/loc only with a real session id; see snabbsokSearchRequest.
		const {
			params,
			impressionId: newImpressionId,
			headers,
		} = snabbsokSearchRequest({
			query,
			region,
			sessionId,
			location: plausibleLocation,
		})
		// Null the ref while the new fetch is in flight: if the user clicks rating
		// on STILL-VISIBLE results from the previous query, handleRating reads null
		// and bails (better to lose the rating than mis-attribute it to the new
		// impression). The ref is set to `newImpressionId` only after `setResults`
		// lands, when the visible results match the id.
		impressionIdRef.current = null
		impressionStartedAtRef.current = Date.now()
		setLoading(true)
		if (sessionId) {
			// The client reports the minimized verdict here; the loader stores it
			// on the impression row. Gated on `!expanded`: after the user
			// expands, a region change re-fetches (new impressionId → new row) —
			// without this guard that row would be mislabelled
			// `minimized=true` for a fully-expanded card the user is viewing (the
			// expand beacon only flips the row that existed at click time, not the
			// new one). `expanded` is read here but deliberately NOT a fetch dep
			// (see below).
			if (!expanded) params.set('minimized', '1')
		}
		fetch(`/resources/snabbsok?${params.toString()}`, {
			signal: ctrl.signal,
			headers,
		})
			.then(r =>
				r.ok
					? (r.json() as Promise<SnabbResults>)
					: Promise.reject(new Error('snabbsok failed')),
			)
			.then(r => {
				setResults(r)
				// Only NOW does the visible card match `newImpressionId`. Anything
				// the user clicks from this point on (rating, click beacon) will
				// read the correct id.
				impressionIdRef.current = newImpressionId
				setLoading(false)
				setStep(0)
			})
			.catch(() => setLoading(false))
		return () => ctrl.abort()
		// `expanded` is read in the body (the minimized gate above) but intentionally
		// EXCLUDED: expanding must not re-fetch — that would re-mint the
		// impressionId and break the surface_view↔impression join. We only need
		// its current value at the fetch moments that actually happen (first query
		// → always false; region change after expand → true → minimized omitted).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [query, region, sessionId, plausibleLocation])

	// Fire ONE snabbsok_impression per user-issued query, only after the
	// results actually arrive AND are non-empty (an empty pool renders nothing,
	// so it isn't really an impression). Reruns on `results` so a region
	// re-fetch can land — but the ref guard pins us to once per query string,
	// matching the design (region toggle doesn't double-count).
	//
	// We send the DESKTOP-visible row counts (capped by the compact-step
	// DESKTOP_CAP), not the full retrieved pool — so engagement maths divide
	// clicks by what the user actually saw, not what we COULD have shown after
	// "Visa fler". Mobile hides a row via CSS only; we can't read the
	// breakpoint here, so we report the desktop ceiling — matches the
	// `total_shown` semantics used by snabbsok_click.
	useEffect(() => {
		if (!results) return
		// A minimized bar is a surface_view, not an impression: the impression
		// fires only when the full card is actually shown, i.e. AFTER the user
		// expands the bar (the deliberate expand click is what makes it a
		// genuine view).
		if (!expanded) return
		if (lastTrackedQuery.current === query) return
		const imVisible = Math.min(results.im.length, DESKTOP_CAP)
		const extVisible = Math.min(results.ext.length, DESKTOP_CAP)
		if (imVisible + extVisible === 0) return
		lastTrackedQuery.current = query
		trackSnabbsokImpression({
			queryLenBucket: queryLenBucket(query),
			imShown: imVisible,
			extShown: extVisible,
			region,
			// Plausible↔Postgres join key. The fetch effect minted it BEFORE
			// the fetch resolved, so it's available by the time this effect
			// runs. Omitted if the fetch path took the no-sid branch (SSR
			// pass before client hydration).
			impressionId: impressionIdRef.current ?? undefined,
			location: plausibleLocation,
		})
	}, [results, query, region, plausibleLocation, expanded])

	// The bar's analogue to the impression above: fire ONE surface_view when the
	// minimized bar is shown for a query. Reuses the EXISTING, already-registered
	// `surface_view` goal (same convention as pwa_install_sheet) with
	// surface='snabbsok_minimized' — no new Plausible goal needed. Together with
	// the `snabbsok_minimized_expanded` button_click (see `expand`) this gives the
	// funnel "bar shown → bar expanded": expand-rate = expanded-visitors /
	// minimized-visitors, mirroring how CTR = click-visitors / impression-
	// visitors. Carries the SAME `impression_id` the eager fetch minted, so the
	// whole chain (minimized → expand → snabbsok_impression → snabbsok_click)
	// shares one join key (Plausible ⟷ Postgres), exactly like snabbsok_impression.
	// Gated on non-empty results — the bar only renders when there's something to
	// reveal — so it never counts a dead-end query.
	useEffect(() => {
		if (!results) return
		if (expanded) return
		if (results.im.length === 0 && results.ext.length === 0) return
		if (lastMinimizedQuery.current === query) return
		lastMinimizedQuery.current = query
		trackSurfaceView({
			surface: 'snabbsok_minimized',
			extraProps: {
				query_len_bucket: queryLenBucket(query),
				region,
				location: plausibleLocation,
				...(impressionIdRef.current
					? { impression_id: impressionIdRef.current }
					: {}),
			},
		})
	}, [results, expanded, query, region, plausibleLocation])

	// Dismiss handler — fires a `button_click` + label='snabbsok_dismissed'
	// Plausible event (same shape as PWA's pwa_prompt_dismissed) so we can
	// measure dismiss-rate against impressions without registering a new
	// custom goal. Position discriminates top-X vs bottom-X.
	const fireDismiss = (position: 'top' | 'bottom') => {
		trackUIEvent({
			eventName: 'button_click',
			label: 'snabbsok_dismissed',
			location: plausibleLocation,
			extraProps: {
				position,
				region,
				...(impressionIdRef.current
					? { impression_id: impressionIdRef.current }
					: {}),
			},
		})
		setDismissed(true)
	}

	// Expand the minimized bar into the full card. The deliberate click is what
	// promotes this query to a real impression (see the impression effect).
	// Tracked as button_click label='snabbsok_minimized_expanded' (same goal +
	// shape as snabbsok_dismissed). It carries the same
	// impression_id as the surface_view above, so the bar-shown → expanded funnel
	// joins exactly per query.
	const expand = () => {
		trackUIEvent({
			eventName: 'button_click',
			label: 'snabbsok_minimized_expanded',
			location: plausibleLocation,
			extraProps: {
				query_len_bucket: queryLenBucket(query),
				region,
				...(impressionIdRef.current
					? { impression_id: impressionIdRef.current }
					: {}),
			},
		})
		// Postgres-side flip: mark this impression row `expanded` so the feedback
		// store distinguishes an opened bar from one never touched (the analogue
		// of the Plausible event above). Guarded on a real id — the ref is set
		// once results land, which is before the bar can be clicked.
		if (impressionIdRef.current) fireFeedbackExpand(impressionIdRef.current)
		setExpanded(true)
	}

	if (dismissed) return null

	// Minimized: a single clickable row instead of the full card. We surface
	// the bar only once results are known non-empty — the same hide-on-empty
	// contract as the card — so the bar never dead-ends into an empty card.
	// While the fetch is in flight we render nothing (no loading flash).
	// `expanded` drops through to the normal card path below.
	if (!expanded) {
		if (!results) return null
		if (results.im.length === 0 && results.ext.length === 0) return null
		// A plain text link, right-aligned under the question bubble: it reads
		// as part of the question and takes one line. No ✕ — unlike the old
		// boxed row it doesn't sit in anyone's way; the expanded card keeps its
		// own ✕. For a week after the move, a one-time hint points at it.
		return (
			<div className="-mt-1 ml-auto flex flex-col items-end">
				<button
					type="button"
					data-testid="snabbsok-minimized-bar"
					onClick={expand}
					className="group flex items-center gap-1 rounded px-1 text-[12px] text-muted-foreground hover:text-foreground"
				>
					<ZapIcon className="h-3 w-3 text-green-600 dark:text-green-500" />
					<span>Visa sökträffar för frågan</span>
					<ChevronDownIcon className="h-3 w-3 transition-transform group-hover:translate-y-px" />
				</button>
				<MovedHint />
			</div>
		)
	}

	if (loading && !results) {
		return (
			<div className="snabbsok-card mt-2 mb-2 rounded-lg border border-border bg-muted/30 px-2.5 py-1 text-[11px] text-muted-foreground/70">
				<div className="flex items-center justify-center gap-1.5">
					<ZapIcon className="w-3 h-3" />
					<span>Snabbsök…</span>
				</div>
			</div>
		)
	}
	if (!results) return null
	const empty = results.im.length === 0 && results.ext.length === 0
	if (empty) return null

	// How many rows to show per section. step 0 = compact (CSS handles 2/3);
	// step ≥ 1 = JS-controlled DESKTOP_CAP + step*STEP.
	const shownPerSection =
		step === 0 ? DESKTOP_CAP : MOBILE_CAP + step * PROGRESSIVE_STEP
	// Combined visible-row count for Plausible's `total_shown` prop. Mirrors
	// the desktop-visible pool (mobile hides a subset via CSS; JS can't see the
	// breakpoint). Used to bucket position clicks against the surfaced list,
	// not the deeper pool the user would reveal with "Visa fler".
	const visibleIm = Math.min(results.im.length, shownPerSection)
	const visibleExt = Math.min(results.ext.length, shownPerSection)
	const totalShown = visibleIm + visibleExt

	// "Visa fler" gating, per breakpoint (mobile cap is lower so it can have
	// hidden rows where desktop doesn't). When still compact we compare to the
	// breakpoint caps; once progressively expanding, to shownPerSection.
	const hiddenMobile =
		hiddenAtCap(results, step === 0 ? MOBILE_CAP : shownPerSection)
	const hiddenDesktop =
		hiddenAtCap(results, step === 0 ? DESKTOP_CAP : shownPerSection)
	const canExpand = hiddenMobile > 0 || hiddenDesktop > 0

	return (
		<div
			data-testid="snabbsok-card"
			className="snabbsok-card mt-2 mb-2 rounded-lg border border-border bg-card/90 backdrop-blur-sm shadow-sm"
		>
			{/* Header: Snabbsök label on the LEFT, region selector + ✕ on the
			    RIGHT (region before the ✕, never covering it). Reading L→R signals
			    "this is the quick-search field"; the left/right split removes the
			    label↔region collision the centered layout had on mobile. */}
			<div className="flex items-center justify-between gap-2 px-2 py-1 border-b border-border/50">
				<div className="flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
					<ZapIcon className="w-3 h-3" />
					<span>Snabbsök</span>
					<InfoTooltip label="Om snabbsök">
						Snabbsöker i Internetmedicin och andra betrodda källor.
						Klicka direkt på en träff för att gå dit, eller läs
						AI-assistentens svar under sökrutan.
					</InfoTooltip>
				</div>
				<div className="flex items-center gap-1.5">
					<RegionBadge region={region} />
					<button
						type="button"
						onClick={() => fireDismiss('top')}
						aria-label="Dölj snabbsök"
						className="text-muted-foreground/70 hover:text-foreground p-1 -mr-0.5 rounded"
					>
						<XIcon className="w-4 h-4" />
					</button>
				</div>
			</div>

			<div className="px-2.5 py-1.5">
				<div className="space-y-1.5">
					{results.im.length > 0 && (
						<SnabbSection
							label="Internetmedicin"
							accent="green"
							hits={results.im}
							step={step}
							shownPerSection={shownPerSection}
							positionOffset={0}
							totalShown={totalShown}
							region={region}
							location={plausibleLocation}
							sessionId={sessionId}
							impressionIdRef={impressionIdRef}
							impressionStartedAtRef={impressionStartedAtRef}
						/>
					)}
					{/* IM-only "Visa fler" between the IM list and the Externa
					    källor heading — only after the first expansion (keeps the
					    initial box small) and only while IM still has hidden rows,
					    so an IM-focused reader (esp. on mobile) can expand in place
					    without scrolling to the bottom control. */}
					{step > 0 &&
						results.ext.length > 0 &&
						results.im.length > shownPerSection && (
							<MidExpand onMore={() => setStep(s => s + 1)} />
						)}
					{results.ext.length > 0 && (
						<SnabbSection
							label="Externa betrodda källor"
							accent="orange"
							hits={results.ext}
							step={step}
							shownPerSection={shownPerSection}
							/* External rows are positioned AFTER the visible IM rows
							   in the rendered list, so their plausible `position` is
							   1-based across the combined visible pool. */
							positionOffset={visibleIm}
							totalShown={totalShown}
							region={region}
							location={plausibleLocation}
							sessionId={sessionId}
							impressionIdRef={impressionIdRef}
							impressionStartedAtRef={impressionStartedAtRef}
						/>
					)}
				</div>

				{/* Bottom row: "Visa fler"/exhausted state on the LEFT, rating widget
				    on the RIGHT. Rendered whenever the card has hits (not just when
				    expandable) so even small result lists get a rating widget. */}
				<ExpandControl
					canExpand={canExpand}
					hiddenMobile={hiddenMobile}
					hiddenDesktop={hiddenDesktop}
					expanded={step > 0}
					onMore={() => setStep(s => s + 1)}
					onClose={() => fireDismiss('bottom')}
					totalCount={results.im.length + results.ext.length}
					query={query}
					results={results}
					region={region}
					location={plausibleLocation}
					impressionIdRef={impressionIdRef}
					ratingResetKey={query}
				/>
			</div>
		</div>
	)
}

/**
 * One-time hint under the text link: "the search results moved here". Shown
 * at most once per browser and only until MOVED_HINT_UNTIL (see
 * app/utils/search-moved-hint.ts). Marked as seen the moment it shows, so a
 * reload never brings it back; it fades after 9 s (hover/touch pauses the
 * fade) or closes on ✕. Unmounts with the link when the card is expanded.
 */
function MovedHint() {
	const [show, setShow] = useState<boolean>(() => {
		if (typeof window === 'undefined' || !movedHintActive()) return false
		try {
			return window.localStorage.getItem(MOVED_HINT_KEY) == null
		} catch {
			return false
		}
	})
	const [fading, setFading] = useState(false)
	const hoveredRef = useRef(false)

	useEffect(() => {
		if (!show) return
		try {
			window.localStorage.setItem(MOVED_HINT_KEY, '1')
		} catch {
			/* ignore */
		}
		let doneTimer: ReturnType<typeof setTimeout> | undefined
		const fadeTimer = setTimeout(() => {
			if (hoveredRef.current) return
			setFading(true)
			doneTimer = setTimeout(() => {
				if (!hoveredRef.current) setShow(false)
			}, 500)
		}, 9000)
		return () => {
			clearTimeout(fadeTimer)
			if (doneTimer) clearTimeout(doneTimer)
		}
	}, [show])

	if (!show) return null
	return (
		<div
			role="status"
			data-testid="snabbsok-moved-hint"
			onMouseEnter={() => {
				hoveredRef.current = true
				setFading(false)
			}}
			onMouseLeave={() => {
				hoveredRef.current = false
			}}
			onTouchStart={() => {
				// Self-clearing lock: the tap itself must not pin the hint forever.
				hoveredRef.current = true
				setTimeout(() => {
					hoveredRef.current = false
				}, 800)
			}}
			className={`relative mt-1.5 flex max-w-xs items-start gap-1.5 rounded-md border border-green-600/40 bg-green-600/10 px-2.5 py-2 text-[11px] leading-snug text-green-800 shadow-sm transition-opacity duration-500 dark:border-green-600/50 dark:bg-green-600/15 dark:text-green-300 after:absolute after:-top-[5px] after:right-5 after:h-2.5 after:w-2.5 after:rotate-45 after:rounded-tl-[2px] after:border-l after:border-t after:border-green-600/40 after:bg-green-50 after:content-[''] dark:after:border-green-600/50 dark:after:bg-green-950 ${
				fading ? 'opacity-0' : 'opacity-100'
			}`}
		>
			<span className="flex-1">
				Sökträffarna har flyttat hit. Klicka på länken för att visa dem.
			</span>
			<button
				type="button"
				onClick={() => setShow(false)}
				aria-label="Dölj meddelande"
				className="-mr-1 -mt-0.5 shrink-0 rounded p-0.5 text-green-700/60 hover:text-green-700 dark:text-green-400/60 dark:hover:text-green-400"
			>
				<XIcon className="h-3 w-3" />
			</button>
		</div>
	)
}

/** Rows hidden across both sections at a given per-section cap. */
function hiddenAtCap(results: SnabbResults, cap: number): number {
	return (
		Math.max(0, results.im.length - cap) +
		Math.max(0, results.ext.length - cap)
	)
}

/**
 * Progressive "Visa fler" control (R4: collapse removed).
 *
 * - "Visa fler" only — expanding is the natural action; there's no "Visa färre"
 *   (the user found collapsing confusing/unwanted — they close the whole card
 *   with the ✕ instead).
 * - While compact (step 0) the number hidden differs per viewport (mobile caps
 *   at 2, desktop at 3), so the control gates itself by CSS: it shows on a
 *   breakpoint only where rows are actually collapsed there (no no-op button),
 *   with a per-breakpoint count.
 * - Once expanding (step ≥ 1) the reveal is JS-controlled + identical on both
 *   viewports. Each click reveals +PROGRESSIVE_STEP more until the pool is
 *   exhausted (canExpand false) — then the button is replaced by a
 *   "Visar nu samtliga N träffar" line + a bottom close link, so the user can
 *   close from where they are without scrolling back to the top ✕.
 */
function ExpandControl({
	canExpand,
	hiddenMobile,
	hiddenDesktop,
	expanded,
	onMore,
	onClose,
	totalCount,
	query,
	results,
	region,
	location,
	impressionIdRef,
	ratingResetKey,
}: {
	canExpand: boolean
	hiddenMobile: number
	hiddenDesktop: number
	expanded: boolean
	onMore: () => void
	onClose: () => void
	totalCount: number
	/** Current search query - persisted on the rating row so analytics + the
	 *  webhook sink see the exact text the user rated (parallels chat-rating's
	 *  question field). */
	query: string
	/** Results visible at rating time - serialised into the answer column so
	 *  the rating row carries the full "what the user saw" snapshot, same way
	 *  chat-rating denormalises the AI answer. */
	results: SnabbResults
	region: string
	location: string
	impressionIdRef: MutableRefObject<string | null>
	/** Per-query value (the query string) used as the rating widget's React
	 *  `key` so a new search remounts it and clears stale rating state. */
	ratingResetKey: string
}) {
	const exhausted = expanded && !canExpand
	// Left slot ("Visa fler" / exhausted text). While compact it self-hides on
	// desktop when nothing is collapsed there (preserves the old per-breakpoint
	// behaviour); the slot can be empty, the right-side rating widget still shows.
	const leftSlotVis = expanded || hiddenDesktop > 0 ? 'flex' : 'flex sm:hidden'
	// R2.2 layout contract (Johannes): the rating widget stays RIGHT-anchored
	// through EVERY state (thumbs → role → comment → tack). It grows VERTICALLY
	// in place; it never wraps to its own full-width line. So the row is
	// `flex-row items-start justify-between` on all viewports (NO flex-wrap, NO
	// basis-full). "Visa fler" pins top-left in a min-height slot so the row stays
	// balanced even when the widget is just the one-line thumbs step.
	//
	// R2.3 #26 EXCEPTION (mobile only): the comment step needs the full card width
	// (fixed-width input in the narrow slot was too cramped). So when commenting,
	// on MOBILE the row becomes flex-col — "Visa fler" on row 1, the widget's
	// comment column full-width on row 2. Desktop (`sm:`) keeps the R2.2 row.
	const [commentActive, setCommentActive] = useState(false)
	return (
		<div
			className={`mt-1.5 flex items-start gap-x-2 gap-y-1 border-t border-border/50 pt-1.5 sm:flex-row sm:justify-between ${
				commentActive ? 'flex-col' : 'flex-row justify-between'
			}`}
		>
			{/* When commenting on MOBILE this slot becomes the full row 1: "Visa fler"
			    left + the comment prompt right (#26, Johannes 2-row spec). The
			    `commentActive` full-width is mobile-only (sm:w-auto). */}
			<div
				className={`${leftSlotVis} min-h-[32px] flex-row items-center gap-1 ${
					commentActive ? 'w-full sm:w-auto sm:shrink-0' : 'shrink-0'
				}`}
			>
				{canExpand && (
					<button
						type="button"
						onClick={onMore}
						data-testid="snabbsok-expand"
						className="group flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted"
						aria-label="Visa fler resultat"
					>
						{expanded ? (
							// JS-controlled reveal: same on both viewports.
							<span>Visa fler</span>
						) : (
							<>
								<span className="sm:hidden">
									Visa fler ({hiddenMobile})
								</span>
								<span className="hidden sm:inline">
									Visa fler ({hiddenDesktop})
								</span>
							</>
						)}
						<ChevronDownIcon className="w-3.5 h-3.5" />
					</button>
				)}
				{exhausted && (
					<>
						<span className="text-[11px] font-medium text-muted-foreground">
							Visar nu samtliga {totalCount} träffar
						</span>
						<button
							type="button"
							onClick={onClose}
							data-testid="snabbsok-close-bottom"
							className="flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
						>
							<XIcon className="w-3.5 h-3.5" />
							<span>Klicka här för att stänga sökrutan</span>
						</button>
					</>
				)}
				{/* Comment prompt on row 1, right of "Visa fler" — MOBILE ONLY (#26).
				    Desktop renders the prompt inside the widget instead. */}
				{commentActive && (
					<span className="ml-auto text-[10px] text-muted-foreground sm:hidden">
						Beskriv gärna ditt betyg{' '}
						<span className="text-muted-foreground/70">(valfritt)</span>
					</span>
				)}
			</div>
			{/* Right-anchored through every state (R2.2). The widget grows vertically
			    in its own right column. R2.3 #26: on MOBILE during the comment step
			    this column goes full-width (w-full) so the input isn't cramped;
			    desktop stays auto-width + right-anchored. */}
			<div
				className={`flex justify-end ${commentActive ? 'w-full sm:w-auto' : ''}`}
			>
				{/* key per query: a new user query remounts the widget and resets its
				    internal state — without it, rating/role/comment/thank-you from
				    query A leaks onto query B's results (QA cross-query state-leak).
				    We key on `query` (a render-driven prop) rather than
				    impressionIdRef.current — a ref mutation doesn't reliably coincide
				    with the parent re-render, so a ref-key fails to remount. Remount
				    fires no Plausible event (trackSnabbsokRating runs only on click). */}
				<SnabbsokRatingWidget
					key={ratingResetKey}
					query={query}
					results={results}
					region={region}
					location={location}
					impressionIdRef={impressionIdRef}
					onCommentActiveChange={setCommentActive}
				/>
			</div>
		</div>
	)
}

/**
 * Rating widget for the Snabbsök card's bottom row (Task #4). Thumbs up/down on
 * result quality, then an optional role step, then an optional comment, then a
 * thank-you that auto-dismisses. Simplified mirror of message-rating.tsx:
 * compact (icon-only on mobile, label on desktop) and writes to a SEPARATE store
 * (`/resources/snabbsok-rating`) — search-result feedback, not answer feedback.
 *
 * The `impressionId` join key is the non-negotiable field: it lets a rating be
 * joined back to its impression (and to snabbsok_impressions) for rating-rate
 * analysis. Read from the ref at click time so the freshest fetch's id is used.
 */
function SnabbsokRatingWidget({
	query,
	results,
	region,
	location,
	impressionIdRef,
	onCommentActiveChange,
}: {
	/** Current search query - sent on rating POST so the row carries what the
	 *  user actually rated (parallels chat-rating.question). */
	query: string
	/** Results snapshot at rating time - serialised in the POST so the row
	 *  carries the full "what the user saw" picture (parallels
	 *  chat-rating.answer). */
	results: SnabbResults
	region: string
	location: string
	impressionIdRef: MutableRefObject<string | null>
	/** Reports when the comment step is active so the parent row can give it the
	 *  full card width on MOBILE (#26): a fixed-width input in the narrow right
	 *  slot was too cramped and the prompt overran the card. Desktop is
	 *  unaffected — the parent only stacks on `max-sm`. */
	onCommentActiveChange?: (active: boolean) => void
}) {
	const [rating, setRating] = useState<'positive' | 'negative' | null>(null)
	const [role, setRole] = useState<
		'healthcare' | 'student' | 'non_healthcare' | null
	>(null)
	const [comment, setComment] = useState('')
	const [showThankYou, setShowThankYou] = useState(false)
	// Terminal state (#18): once the flow ends (Send, or a ✕ after rating), the
	// widget collapses for good. Earlier steps must NOT be reachable again — the
	// old bug was that thank-you's auto-dismiss set showThankYou=false, which fell
	// back to the comment step because rating/role were still set.
	const [done, setDone] = useState(false)

	// Comment step = rated + role chosen + not yet thanked/done. Reported up so
	// the parent row can widen this column on mobile (#26).
	const commentActive = !done && !showThankYou && rating !== null && role !== null
	useEffect(() => {
		onCommentActiveChange?.(commentActive)
	}, [commentActive, onCommentActiveChange])

	// Same race-closing pattern as message-rating.tsx: the POST id is captured as
	// an awaitable Promise so a fast role/comment click can await it instead of
	// dropping the PATCH when the user clicks faster than the POST round-trip.
	const ratingIdPromiseRef = useRef<Promise<string | null> | null>(null)

	// Auto-collapse the thank-you after 3s into the terminal `done` state (NOT
	// back to a prior step). Clear the timer on unmount.
	useEffect(() => {
		if (!showThankYou) return
		const t = setTimeout(() => setDone(true), 3000)
		return () => clearTimeout(t)
	}, [showThankYou])

	const handleRating = (next: 'positive' | 'negative') => {
		setRating(next)
		// Race-defense: if a newer query is in flight, impressionIdRef is null
		// (cleared in the fetch effect). Bail BEFORE tracking + posting so the
		// Plausible event count stays reconcilable with chat_ratings rows -
		// otherwise the race window logs a rating event with no DB sibling.
		if (!impressionIdRef.current) return
		trackSnabbsokRating({
			rating: next,
			impressionId: impressionIdRef.current ?? undefined,
			region,
			location,
		})
		// Compact snapshot of the results the user is rating. `title` stays the
		// RAW chunk title for longitudinal comparability with pre-aug-2026
		// rows; `label` is what the user actually SAW - the REG-6-qualified
		// display label, same resolution the render path uses. Both are
		// needed: snabbsok_impressions stores NO titles at all (packResultRow
		// whitelists position/url/source/ids), so this snapshot is the only
		// sink that records the rated labels.
		const imLabels = disambiguateSameSource(results.im, true)
		const extLabels = disambiguateSameSource(results.ext, false)
		const resultsSnapshot = {
			im: results.im.map(h => ({
				title: h.title,
				label: imLabels.get(h) ?? displayTitle(h, true),
				url: h.url,
				source: h.source,
			})),
			ext: results.ext.map(h => ({
				title: h.title,
				label: extLabels.get(h) ?? displayTitle(h, false),
				url: h.url,
				source: h.source,
			})),
		}
		ratingIdPromiseRef.current = (async () => {
			try {
				const response = await fetch('/resources/snabbsok-rating', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						...(isTestingMode() ? { 'x-im-testing': '1' } : {}),
					},
					body: JSON.stringify({
						rating: next,
						impressionId: impressionIdRef.current,
						query,
						results: resultsSnapshot,
						region,
					}),
				})
				const data = (await response.json()) as { id?: string }
				return data.id ?? null
			} catch {
				return null
			}
		})()
	}

	const handleRole = async (next: 'healthcare' | 'student' | 'non_healthcare') => {
		setRole(next)
		const id = ratingIdPromiseRef.current
			? await ratingIdPromiseRef.current
			: null
		if (!id) return
		try {
			await fetch('/resources/snabbsok-rating', {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ id, role: next }),
			})
		} catch {
			/* fail silently */
		}
	}

	const handleComment = async () => {
		if (!comment.trim()) return
		const id = ratingIdPromiseRef.current
			? await ratingIdPromiseRef.current
			: null
		if (id) {
			try {
				await fetch('/resources/snabbsok-rating', {
					method: 'PATCH',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ id, comment: comment.trim() }),
				})
			} catch {
				/* fail silently */
			}
		}
		setShowThankYou(true)
	}

	// Terminal: flow finished — render nothing (the card itself stays). No prior
	// step is reachable from here (#18).
	if (done) return null

	// Thank-you (#23): inline + right-anchored in the same slot as the other
	// steps — never a centered new row. Height ≈ thumbs step so nothing jumps.
	if (showThankYou) {
		return (
			<div
				role="status"
				data-testid="snabbsok-rating-thanks"
				className="flex min-h-[32px] items-center justify-end gap-1.5 text-[11px] text-muted-foreground sm:min-h-0"
			>
				<span>Tack för din feedback!</span>
				<button
					type="button"
					onClick={() => setDone(true)}
					aria-label="Stäng"
					className="rounded p-0.5 text-muted-foreground/60 hover:text-foreground"
				>
					<XIcon className="w-3.5 h-3.5" />
				</button>
			</div>
		)
	}

	// Step 3 — comment (after role chosen or skipped).
	// MOBILE (#26, Johannes 2-row): the prompt sits on ROW 1 beside "Visa fler"
	//   (rendered by ExpandControl), so here we render ONLY the full-width input
	//   row — it becomes row 2.
	// DESKTOP (R2.2 #22, unchanged): right-anchored vertical stack with the prompt
	//   on top (the ExpandControl prompt is sm:hidden, this one is hidden sm:*).
	if (rating && role) {
		return (
			<div className="flex w-full flex-col items-stretch gap-1 sm:w-auto sm:items-end">
				<span className="hidden text-[10px] text-muted-foreground sm:inline sm:text-right">
					Beskriv gärna ditt betyg{' '}
					<span className="text-muted-foreground/70">(valfritt)</span>
				</span>
				<div className="flex w-full items-center gap-1.5 sm:w-auto">
					<input
						type="text"
						value={comment}
						onChange={e => setComment(e.target.value)}
						onKeyDown={e => {
							if (e.key === 'Enter' && comment.trim()) handleComment()
						}}
						placeholder="Skriv här..."
						data-testid="snabbsok-rating-comment"
						className="h-9 sm:h-7 w-full sm:w-[14rem] flex-1 sm:flex-none rounded-md border border-input bg-background px-2 text-[12px] outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring"
					/>
					<button
						type="button"
						onClick={handleComment}
						disabled={!comment.trim()}
						aria-label="Skicka"
						data-testid="snabbsok-rating-comment-submit"
						className={`flex min-h-[40px] min-w-[40px] sm:min-h-0 sm:min-w-0 shrink-0 items-center justify-center rounded p-1 ${
							comment.trim()
								? 'text-foreground hover:bg-muted'
								: 'text-muted-foreground/30'
						}`}
					>
						<SendIcon className="w-3.5 h-3.5" />
					</button>
					<button
						type="button"
						onClick={() => setShowThankYou(true)}
						aria-label="Stäng"
						className="flex min-h-[40px] min-w-[40px] sm:min-h-0 sm:min-w-0 shrink-0 items-center justify-center rounded p-1 text-muted-foreground/60 hover:text-foreground"
					>
						<XIcon className="w-3.5 h-3.5" />
					</button>
				</div>
			</div>
		)
	}

	// Step 2 — role (after a rating is set). Both viewports show icon + visible
	// text (R2.2 #21 — no tooltip): SHORT label on mobile (#13), FULL label on
	// desktop. Full role name is also on aria-label for screen readers.
	if (rating) {
		const roleOptions = [
			{
				value: 'healthcare' as const,
				Icon: StethoscopeIcon,
				label: 'Vårdpersonal',
				short: 'Vårdpers.',
			},
			{
				value: 'student' as const,
				Icon: GraduationCapIcon,
				label: 'Studerande',
				short: 'Student',
			},
			{
				value: 'non_healthcare' as const,
				Icon: UserIcon,
				label: 'Ej vårdpersonal',
				short: 'Ej vård',
			},
		]
		return (
			// Right-anchored block that grows vertically (R2.2 #20): prompt on top,
			// role buttons below. Each button shows icon + text — SHORT label on
			// mobile (#13), FULL label on desktop (#21, no tooltip).
			<div className="flex flex-col items-end gap-1">
				<span className="text-[10px] text-muted-foreground">
					Vilken roll har du?{' '}
					<span className="text-muted-foreground/70">(valfritt)</span>
				</span>
				<div className="flex items-center gap-1.5">
					{roleOptions.map(({ value, Icon, label, short }) => (
						<button
							key={value}
							type="button"
							onClick={() => handleRole(value)}
							aria-label={label}
							data-testid={`snabbsok-role-${value}`}
							className="flex min-h-[40px] flex-col items-center justify-center gap-0.5 rounded border border-border/60 px-1.5 py-1 text-green-600 hover:border-green-600 hover:bg-muted sm:min-h-0 sm:flex-row sm:gap-1"
						>
							<Icon className="w-4 h-4 shrink-0" />
							<span className="text-[9px] leading-tight text-foreground/80 sm:hidden">
								{short}
							</span>
							<span className="hidden text-[11px] leading-tight text-foreground/80 sm:inline">
								{label}
							</span>
						</button>
					))}
					<button
						type="button"
						onClick={() => setShowThankYou(true)}
						aria-label="Hoppa över"
						className="rounded p-1 text-muted-foreground/60 hover:text-foreground"
					>
						<XIcon className="w-3.5 h-3.5" />
					</button>
				</div>
			</div>
		)
	}

	// Step 1 — thumbs (default).
	return (
		<div className="flex items-center gap-1.5">
			<span className="hidden sm:inline text-[10px] text-muted-foreground">
				Vad tyckte du om sökresultaten?
			</span>
			<button
				type="button"
				onClick={() => handleRating('positive')}
				aria-label="Tumme upp"
				data-testid="snabbsok-rating-up"
				className="flex min-h-[40px] min-w-[40px] sm:min-h-0 sm:min-w-0 items-center justify-center rounded p-1 text-muted-foreground hover:bg-green-50 hover:text-green-600 dark:hover:bg-green-950/40"
			>
				<ThumbsUpIcon className="w-4 h-4" />
			</button>
			<button
				type="button"
				onClick={() => handleRating('negative')}
				aria-label="Tumme ner"
				data-testid="snabbsok-rating-down"
				className="flex min-h-[40px] min-w-[40px] sm:min-h-0 sm:min-w-0 items-center justify-center rounded p-1 text-muted-foreground hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40"
			>
				<ThumbsDownIcon className="w-4 h-4" />
			</button>
		</div>
	)
}

/**
 * IM-only "Visa fler" placed between the IM section and the Externa betrodda källor
 * heading. Appears only AFTER the first expansion (so the initial box stays
 * small) and only while IM still has hidden rows, letting an IM-focused reader
 * (esp. on mobile) keep expanding the IM list in place. Emerald to tie it to
 * the IM section; clicking advances the shared step (+PROGRESSIVE_STEP rows).
 */
function MidExpand({ onMore }: { onMore: () => void }) {
	return (
		<div className="flex justify-start border-t border-border/50 pt-1.5">
			<button
				type="button"
				onClick={onMore}
				data-testid="snabbsok-expand-im"
				className="group flex min-h-[40px] sm:min-h-0 items-center gap-1 rounded px-2 py-1 text-[11px] font-medium text-green-700 hover:bg-green-50 hover:text-green-900 dark:text-green-400 dark:hover:bg-green-950/50 dark:hover:text-green-300"
				aria-label="Visa fler Internetmedicin-resultat"
			>
				<span>Visa fler</span>
				<ChevronDownIcon className="w-3.5 h-3.5" />
			</button>
		</div>
	)
}

/**
 * One result row — title + sitelinks, prefixed by the per-source colour chip.
 * No body text (titles + sitelinks only, by design). On desktop the row is a
 * HoverCard trigger that previews the full source name + title + url; on mobile
 * (no hover) the title link is tapped straight through.
 */
function SnabbRow({
	hit,
	isIM,
	showSitelinks,
	label,
	position,
	positionInSection,
	totalShown,
	region,
	location,
	sessionId,
	impressionIdRef,
	impressionStartedAtRef,
}: {
	hit: SnabbHit
	isIM: boolean
	showSitelinks: boolean
	// REG-6: optional pre-resolved display label (carries a same-source
	// duplicate-label qualifier when this row collides with a sibling). Falls
	// back to the plain displayTitle when absent (e.g. tests rendering a lone row).
	label?: string
	/** 1-based position across the combined visible list, for snabbsok_click. */
	position: number
	/** 1-based position WITHIN this row's section (im or ext) — the
	 *  feedback-DB schema's `clicked_position` is section-local, NOT
	 *  combined-list. Two semantics, two sinks. */
	positionInSection: number
	totalShown: number
	region: string
	location: string
	sessionId: string
	impressionIdRef: MutableRefObject<string | null>
	impressionStartedAtRef: MutableRefObject<number | null>
}) {
	const title = label ?? displayTitle(hit, isIM)
	// IM rows label the chip "IM"; external rows show the (shortened) source.
	const source = isIM ? 'Internetmedicin' : hit.source || ''
	const abbrev = isIM ? 'IM' : shortSourceName(hit.source)
	const color = sourceColor(source)
	const sitelinks = hit.sitelinks ?? []

	/** Compose the feedback-store click payload from refs (read AT CLICK
	 *  TIME so impressionId + started-at reflect the freshest fetch, not
	 *  whatever was current at this row's render). Both `clickedUrl` and
	 *  the URL passed to Plausible point at the same href (hit.url), but
	 *  Plausible strips fragment for partner-rollup rollups while the
	 *  feedback row keeps the full URL — Plausible-side stripping lives
	 *  in trackSnabbsokClick. */
	const composeFeedbackPayload = (
		url: string,
		isSitelink: boolean,
		sitelinkIndex: number | null,
	): SnabbsokClickPayload =>
		snabbsokClickPayload({
			impressionId: impressionIdRef.current,
			sessionId,
			clickedPosition: positionInSection,
			clickedUrl: url,
			clickedSource: source,
			clickedSourceType: isIM ? 'im' : 'external',
			clickedTopicId: hit.topic_id ?? null,
			isSitelink,
			sitelinkIndex,
			startedAt: impressionStartedAtRef.current,
			region,
		})

	// Common click payload for title + hovercard-URL clicks on this row. We do
	// NOT block default navigation (target="_blank" still wins) — Plausible's
	// transport is sendBeacon (and so is the feedback POST), so both events
	// survive the navigation away.
	const trackTitleClick = () => {
		trackSnabbsokClick({
			destinationUrl: hit.url,
			destinationSource: source,
			sourceType: isIM ? 'im' : 'external',
			position,
			totalShown,
			region,
			isSitelink: false,
			impressionId: impressionIdRef.current ?? undefined,
			location,
		})
		// Sessionless requests (SSR pass that somehow rendered an
		// interactive row) cannot satisfy the server zod's
		// `sessionId: z.string().uuid()`, so we skip — feedback is opt-in
		// the moment we have a real session id. DNT check is in fireFeedbackClick.
		if (sessionId) {
			fireFeedbackClick(composeFeedbackPayload(hit.url, false, null))
		}
	}

	// A speciality landing page is an INGÅNG (a subject area with its list of
	// overviews), not a treatment overview — without a marker it reads as a
	// suspiciously thin article. The muted tag sets the expectation. It renders
	// as a SIBLING of the link, never inside it: text-decoration propagates from
	// the <a> and a descendant cannot cancel it (the tag would underline on
	// hover), and the tag must not join the link's accessible name. The label
	// matches the search dialog's badge for the same index type
	// (search-dialog.tsx: type === 'SPECIALITY' → "Specialitet").
	const isSpecialityPage = hit.type === 'SPECIALITY'

	const titleLink = (
		<a
			href={hit.url}
			target="_blank"
			rel="noopener noreferrer"
			onClick={trackTitleClick}
			// Tight single line on BOTH viewports so the sitelinks hug the title
			// exactly like desktop (R4b final notch — the min-h box was leaving a
			// ~20px gap on mobile). Any mobile tap-area bump goes as a small TOP
			// padding only (pt-0.5), so it never pushes the sitelinks away below;
			// the comfortable 40px touch targets live on the sitelinks + chip.
			className="block truncate pt-0.5 sm:pt-0 text-[12.5px] leading-snug text-blue-700 dark:text-blue-400 hover:underline"
		>
			<span className="truncate">{title}</span>
		</a>
	)

	const titleWithHover = (
		<RowHoverCard
			hit={hit}
			source={source}
			color={color}
			title={title}
			onUrlClick={trackTitleClick}
		>
			{titleLink}
		</RowHoverCard>
	)

	return (
		// pb groups each [result + its sitelinks]: tight inside (title hugs its
		// sitelinks), clear separation BELOW before the next result. Heavier on
		// desktop where rows are dense, so the grouping reads.
		<li className="min-w-0 pb-1 sm:pb-2.5">
			<div className="flex items-stretch gap-1.5 min-w-0">
				<SourceChip color={color} abbrev={abbrev} />
				<div className="min-w-0 flex-1">
					{/* Desktop: wrap the title in a hover preview. The HoverCard is
					    pointer-driven, so on touch devices it never opens and the
					    tap falls through to the link — exactly the mobile behaviour
					    we want. */}
					{!isSpecialityPage ? (
						titleWithHover
					) : (
						// overflow-hidden restores the clipping boundary the plain
						// block-truncate anchor provides in the non-tag branch, so an
						// over-wide tag can never paint outside the card.
						<div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
							<div className="min-w-0">{titleWithHover}</div>
							<span className="shrink-0 text-[9.5px] font-semibold uppercase tracking-wide text-muted-foreground">
								Specialitet
							</span>
						</div>
					)}
					{showSitelinks && sitelinks.length > 0 && (
						<SiteLinks
							sitelinks={sitelinks}
							parentSource={source}
							sourceType={isIM ? 'im' : 'external'}
							parentPosition={position}
							totalShown={totalShown}
							region={region}
							location={location}
							impressionId={impressionIdRef.current ?? undefined}
							onSitelinkClick={(url, sitelinkIndex0Based) => {
								if (!sessionId) return
								// Feedback DB wants 1-based sitelink_index (schema 1..20);
								// SiteLinks loops 0-based locally so we +1 here.
								fireFeedbackClick(
									composeFeedbackPayload(
										url,
										true,
										sitelinkIndex0Based + 1,
									),
								)
							}}
						/>
					)}
				</div>
			</div>
		</li>
	)
}

/**
 * Desktop hover preview, reusing the AI assistant's Radix HoverCard
 * (citation-bubble.tsx). Shows the FULL source name spelled out (for when the
 * abbrev isn't familiar) + the full, untruncated title + the url. `openDelay`
 * matches the citation bubble. Pointer-only, so mobile taps pass through to the
 * wrapped link.
 */
function RowHoverCard({
	hit,
	source,
	color,
	title,
	onUrlClick,
	children,
}: {
	hit: SnabbHit
	source: string
	color: string
	title: string
	/** Fires when the URL line inside the hovercard is clicked. Same shape as
	 *  the parent title-link click — same row, same position, same payload. */
	onUrlClick: () => void
	children: React.ReactNode
}) {
	const full = sourceFullName(source)
	return (
		<HoverCard.Root openDelay={200} closeDelay={120}>
			<HoverCard.Trigger asChild>{children}</HoverCard.Trigger>
			<HoverCard.Portal>
				<HoverCard.Content
					side="top"
					align="start"
					sideOffset={6}
					className="z-50 hidden w-80 rounded-lg border bg-white p-3 shadow-lg sm:block animate-in fade-in-0 zoom-in-95"
				>
					<div className="space-y-1.5">
						<div className="flex items-center gap-1.5">
							<span
								className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
								style={{ backgroundColor: color }}
								aria-hidden
							/>
							<span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
								{full}
							</span>
						</div>
						<p className="text-[13px] font-medium leading-snug text-foreground">
							{title}
						</p>
						<a
							href={hit.url}
							target="_blank"
							rel="noopener noreferrer"
							onClick={onUrlClick}
							className="flex items-center gap-1 break-all text-[11px] leading-tight text-blue-600/80 hover:text-blue-700 dark:text-blue-400/80 dark:hover:text-blue-300 hover:underline"
						>
							<ExternalLinkIcon className="h-3 w-3 shrink-0" />
							<span>{hit.url}</span>
						</a>
					</div>
					<HoverCard.Arrow className="fill-white" />
				</HoverCard.Content>
			</HoverCard.Portal>
		</HoverCard.Root>
	)
}

/** Solid rounded per-source colour chip with the abbrev inside it (white text).
 *  Background is an inline hex (the hash-fallback hues aren't Tailwind classes);
 *  white text on every `sourceColor` value meets WCAG AA. Narrower on mobile so
 *  long titles keep more of the scarce phone width. Vertically centred so a row
 *  with sitelinks doesn't get a tall stretched pill. */
function SourceChip({ color, abbrev }: { color: string; abbrev: string }) {
	return (
		<span
			className="flex w-[2.5rem] sm:w-[3rem] shrink-0 self-center items-center justify-center rounded px-1 py-0.5 text-center text-[9px] font-semibold uppercase leading-tight tracking-wide text-white"
			style={{ backgroundColor: color }}
			title={abbrev}
		>
			<span className="line-clamp-2">{abbrev}</span>
		</span>
	)
}

/** Sitelinks under a parent result — an article's section anchors (or a
 *  multi-page family's sub-pages). Cap 2 on mobile, 3 on `sm`+ (the 3rd is
 *  hidden on narrow viewports) — keeps it from becoming a wall of text.
 *
 *  Spacing (R4b — hierarchy): HUG the title above (no top gap; the next
 *  result's bottom-padding lives on the parent `<li>`, giving clear separation
 *  BELOW). Generous horizontal gap so the links don't run together. Each link
 *  is a ~40px tap target on mobile (touch guideline) but the container hugs the
 *  title, so the hit-area grows downward, not as a gap above.
 *
 *  Rendered as a `div` of `<a>` (not `ul`/`li`) on purpose: the result rows are
 *  `<li>` and the e2e test counts visible rows with `ul.first().locator('li')`
 *  — keeping sitelinks out of the `li` tree means they never inflate that
 *  count. */
function SiteLinks({
	sitelinks,
	parentSource,
	sourceType,
	parentPosition,
	totalShown,
	region,
	location,
	impressionId,
	onSitelinkClick,
}: {
	sitelinks: { title: string; url: string }[]
	/** The parent row's source — sitelinks carry the same destination_source
	 *  so the dashboard can read them as "clicks into <source>", not "clicks
	 *  into <untyped sitelink>". */
	parentSource: string
	sourceType: 'im' | 'external'
	/** The parent row's 1-based position in the combined visible list. */
	parentPosition: number
	totalShown: number
	region: string
	location: string
	/** Plausible↔Postgres join key carried into both events. Optional so the
	 *  early-mount window without an impression id doesn't drop the click. */
	impressionId?: string
	/** Feedback-store side-channel: parent owns payload composition so refs
	 *  + sessionId stay scoped to SnabbRow. `sitelinkIndex` is the LOCAL
	 *  0-based loop index — parent +1's it to match the 1..20 schema. */
	onSitelinkClick: (url: string, sitelinkIndex: number) => void
}) {
	return (
		<div className="flex flex-wrap items-center gap-x-4 gap-y-0">
			{sitelinks.slice(0, 3).map((s, j) => (
				<a
					key={s.url}
					href={s.url}
					target="_blank"
					rel="noopener noreferrer"
					onClick={() => {
						trackSnabbsokClick({
							destinationUrl: s.url,
							destinationSource: parentSource,
							sourceType,
							position: parentPosition,
							totalShown,
							region,
							isSitelink: true,
							// NB: sitelink_index divergence by design (PR #37 review).
							// Plausible gets the 0-based loop index `j` (preserves
							// dashboard semantics for the existing analytics setup);
							// the feedback-DB beacon below uses `j + 1` to match the
							// zod 1..20 schema in snabbsok-click.ts. Joining the two
							// stores on sitelink_index therefore requires
							// `plausible.sitelink_index + 1 = postgres.sitelink_index`.
							sitelinkIndex: j,
							impressionId,
							location,
						})
						onSitelinkClick(s.url, j)
					}}
					// 1st & 2nd always; 3rd only on `sm`+ (desktop). ~40px tap target
					// on mobile via min-h; compact on desktop (sm:min-h-0). R4b
					// notch: items-start on mobile pins the link text to the TOP of
					// its tap box so it sits right under the title (which is pinned
					// to the BOTTOM of ITS box) — the residual center-slack gap is
					// gone; the tap height extends DOWNWARD. Desktop centred.
					className={`flex min-h-[28px] sm:min-h-0 items-start sm:items-center max-w-[11rem] min-w-0 truncate text-[11px] text-blue-600/80 dark:text-blue-400/80 hover:underline ${
						j >= 2 ? 'hidden sm:flex' : ''
					}`}
				>
					{s.title}
				</a>
			))}
		</div>
	)
}

/**
 * A labelled section (IM or Externa betrodda källor) with the coloured dot header.
 * Compact (step 0): CSS caps at 2 mobile / 3 desktop. Expanding (step ≥ 1):
 * JS shows `shownPerSection` rows (progressive reveal), no CSS cap.
 */
function SnabbSection({
	label,
	accent,
	hits,
	step,
	shownPerSection,
	positionOffset,
	totalShown,
	region,
	location,
	sessionId,
	impressionIdRef,
	impressionStartedAtRef,
}: {
	label: string
	accent: 'green' | 'orange'
	hits: SnabbHit[]
	step: number
	shownPerSection: number
	/** Position offset for Plausible: external rows are positioned after the
	 *  visible IM rows in the combined list (1-based at the click site). */
	positionOffset: number
	/** Combined visible rows across both sections, for the click event. */
	totalShown: number
	region: string
	location: string
	/** Per-tab session id (UUID v4 from sessionStorage). Empty string during
	 *  the SSR pass; loader/beacon both gate on this. */
	sessionId: string
	/** Refs (not snapshots) so click handlers read the CURRENT impression id
	 *  + started-at clock at click time. The fetch effect mints a new id
	 *  per query; a click within an older render still resolves the freshest
	 *  id this way (refs are stable React objects, .current is mutable). */
	impressionIdRef: MutableRefObject<string | null>
	impressionStartedAtRef: MutableRefObject<number | null>
}) {
	const isIM = accent === 'green'
	// green-600 is overridden to the user-confirmed IM primary #4e7a3f in
	// extended-theme.ts, so the label hex matches the SourceChip exactly.
	const labelClass = isIM
		? 'text-green-600 dark:text-green-400'
		: 'text-orange-700 dark:text-orange-400'
	// REG-6: resolve display labels over the FULL section (before any slice) so a
	// same-source duplicate-label qualifier is stable regardless of how many rows
	// are currently revealed. Display-only — does not reorder or drop rows.
	const labels = disambiguateSameSource(hits, isIM)
	// Once expanding, slice in JS; while compact, render all and let CSS hide.
	const rows = step === 0 ? hits : hits.slice(0, shownPerSection)

	return (
		<div>
			<div
				className={`text-[10px] uppercase tracking-wider font-medium mb-0.5 ${labelClass}`}
			>
				{label}
			</div>
			<ul className="space-y-0">
				{rows.map((h, i) => (
					<div key={h.url} className={step === 0 ? rowVisibility(i) : 'block'}>
						{/* Compact view (step 0): sitelinks only on the FIRST result of
						    the section — the best-surfaced hit, where nav help belongs —
						    so the box stays small. Expanding (step ≥ 1) reveals sitelinks
						    on all rows (space is fine then). */}
						<SnabbRow
							hit={h}
							isIM={isIM}
							showSitelinks={step > 0 || i === 0}
							label={labels.get(h)}
							position={positionOffset + i + 1}
							/* Section-local 1-based position for the feedback-DB
							   schema (its `clicked_position` is per-section). Two
							   different position semantics for two different sinks —
							   schema wants section-local, Plausible wants combined. */
							positionInSection={i + 1}
							totalShown={totalShown}
							region={region}
							location={location}
							sessionId={sessionId}
							impressionIdRef={impressionIdRef}
							impressionStartedAtRef={impressionStartedAtRef}
						/>
					</div>
				))}
			</ul>
		</div>
	)
}

/**
 * Compact (step 0) caps: row 0-1 always, row 2 on `sm`+, rows 3+ hidden. CSS-
 * only so SSR matches the client. (Progressive expand switches to JS slicing.)
 */
function rowVisibility(i: number): string {
	if (i >= 3) return 'hidden'
	if (i >= 2) return 'hidden sm:block'
	return 'block'
}

/**
 * Click-or-hover info popover anchored to a small "(i)" trigger button. Works
 * on touch (Radix Popover opens on tap) AND on desktop (manual mouseenter/leave
 * mirrors a tooltip). The accessible name is supplied via `label` so screen
 * readers announce "Om snabbsök, knapp" rather than just "info".
 */
function InfoTooltip({
	label,
	children,
}: {
	label: string
	children: React.ReactNode
}) {
	const [open, setOpen] = useState(false)
	return (
		<Popover.Root open={open} onOpenChange={setOpen}>
			<Popover.Trigger asChild>
				<button
					type="button"
					aria-label={label}
					onMouseEnter={() => setOpen(true)}
					onMouseLeave={() => setOpen(false)}
					className="inline-flex items-center justify-center text-muted-foreground/60 hover:text-muted-foreground focus:outline-none"
				>
					<InfoIcon className="w-3 h-3" />
				</button>
			</Popover.Trigger>
			<Popover.Portal>
				<Popover.Content
					side="bottom"
					align="start"
					sideOffset={4}
					onMouseEnter={() => setOpen(true)}
					onMouseLeave={() => setOpen(false)}
					className="z-50 max-w-[260px] rounded-lg border bg-white px-3 py-2 text-[11px] normal-case font-normal leading-relaxed tracking-normal text-foreground shadow-lg focus:outline-none dark:border-border dark:bg-popover dark:text-popover-foreground"
				>
					{children}
					<Popover.Arrow className="fill-white dark:fill-popover" />
				</Popover.Content>
			</Popover.Portal>
		</Popover.Root>
	)
}

/**
 * Kortet följer sidans regionval och visar bara VILKEN region som
 * gäller (kartnål + namn + (i)). Bytet görs i chattrutans pill / förstasidans
 * mening, så kortet får ingen egen kontroll — ett val för hela sidan.
 */
function RegionBadge({ region }: { region: string }) {
	const label = REGIONS.find(r => r.value === region)?.label ?? 'Alla regioner'
	const active = region !== 'all'
	return (
		<span
			data-testid="snabbsok-region-badge"
			className="inline-flex items-center gap-1 text-[10px] leading-none"
		>
			<span
				className={
					'inline-flex items-center gap-0.5 ' +
					(active
						? 'font-medium text-[#4E7A3F] dark:text-green-300'
						: 'text-muted-foreground')
				}
			>
				<MapPinIcon className="h-3 w-3" />
				<span className="hidden sm:inline">{label}</span>
				<span className="sm:hidden">{label.replace(/^Region /, '')}</span>
			</span>
			<InfoTooltip label="Om region">
				Snabbsök följer sidans regionval ({label}). Vald region prioriteras
				högre bland träffarna, men du ser fortfarande andra. Byt region i
				chattrutan.
			</InfoTooltip>
		</span>
	)
}

